import { Label } from '#UNSAFE_Label/Label';
import { Link } from '#UNSAFE_Link';
import { Text } from '#UNSAFE_Text';
import { ReadonlyTextField } from '#UNSAFE_TextField';
import { InlineUserAssistance, UserAssistanceDensityType } from '#UNSAFE_UserAssistance';
import { View } from '#UNSAFE_View';
import { useFormContext } from '#hooks/UNSAFE_useFormContext';
import { FormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { useTextField } from '#hooks/UNSAFE_useTextField';
import { Size } from '#utils/UNSAFE_size';
import { LayoutColumnSpan } from '#utils/UNSAFE_styles/Layout';
import { ComponentProps } from 'preact';
import { HiddenLabel } from './HiddenLabel';
import { useLabelledLinkAria } from './useLabelledLinkAria';
import { useLabelledLinkTheme } from './useLabelledLinkTheme';

type PickedLinkProps = Pick<
  ComponentProps<typeof Link>,
  'aria-describedby' | 'href' | 'onClick' | 'target'
>;

export type LabelledLinkProps = PickedLinkProps & {
  /**
   * Specifies the text that should appear in the field. If this property
   * is not provided, then the URL provided using the `href` property
   * will be used as the text in the field.
   */
  children?: string;

  /**
   * Specifies how many columns to span in a FormLayout with direction === 'row'
   */
  columnSpan?: LayoutColumnSpan;

  /**
   * Specifies the label associated with the field.
   */
  label: string;

  /**
   * Specifies where the label is positioned relative to the field.
   * @default 'inside'
   */
  labelEdge?: 'inside' | 'start' | 'top' | 'none';

  /**
   * Specifies the width of the label when <code>labelEdge</code> is <code>"start"</code>.
   */
  labelStartWidth?: Size;

  /**
   * Specifies how to align text within the field.
   */
  textAlign?: 'start' | 'end' | 'right';

  /**
   * The component does not support any assistive text. But, this property can be used to specify
   * whether or not the component should reserve space to be consistent with the other form components
   * in the Form Layout. It can be set to:
   * <ul>
   * <li><code>'efficient'</code>: Reserve space to prevent layout reflow.</li>
   * <li><code>'reflow'</code>: Do not reserve space.</li>
   * <li><code>'compact'</code>: Do not reserve space. </li>
   * </ul>
   */
  userAssistanceDensity?: UserAssistanceDensityType;
};

/**
 * LabelledLink component can be used to render a readonly form control field that
 * has a link for its content.
 */
export const LabelledLink = ({
  'aria-describedby': ariaDescribedBy,
  children,
  columnSpan,
  href,
  label,
  labelEdge: propLabelEdge,
  labelStartWidth: propLabelStartWidth,
  onClick,
  target,
  textAlign: propTextAlign,
  userAssistanceDensity: propUserAssistanceDensity
}: LabelledLinkProps) => {
  // Consume the form context to get the default values
  const {
    isFormLayout,
    isReadonly: isFormReadonly,
    labelEdge: formLabelEdge,
    labelStartWidth: formLabelStartWidth,
    textAlign: formTextAlign,
    userAssistanceDensity: formUserAssistanceDensity
  } = useFormContext();

  // default to FormContext values if component properties are not specified
  const labelEdge = propLabelEdge ?? formLabelEdge;
  const labelStartWidth = propLabelStartWidth ?? formLabelStartWidth;
  const textAlign = propTextAlign ?? formTextAlign;
  const userAssistanceDensity = propUserAssistanceDensity ?? formUserAssistanceDensity;

  // We will use the URL as text if the link text is not provided as a child
  const linkText = children ?? href;

  // Calculate the text field related props
  const { baseId, formFieldContext, labelProps, inputProps, userAssistanceProps } = useTextField({
    ariaDescribedBy,
    isReadonly: true,
    labelEdge,
    value: linkText
  });

  // Create the label component and the fieldLabelProps
  const labelComp = <Label {...labelProps}>{label}</Label>;
  const fieldLabelProps = {
    label: labelEdge !== 'none' ? labelComp : undefined,
    labelEdge: labelEdge !== 'none' ? labelEdge : undefined,
    labelStartWidth: labelEdge !== 'none' ? labelStartWidth : undefined
  };

  // save space for user assistance if density is 'efficient', even though we don't
  // render user assistance for readonly fields for now.
  const inlineUserAssistance =
    userAssistanceDensity === 'efficient' ? (
      <InlineUserAssistance
        userAssistanceDensity={userAssistanceDensity}
        {...userAssistanceProps}
      />
    ) : undefined;

  // Get the theme classes
  const { classes } = useLabelledLinkTheme({
    hasInsideLabel: labelEdge === 'inside',
    isFormLayout,
    isFormReadonly,
    textAlign
  });

  // Get the aria props
  const hasEmptyLabel = label === '';
  const hasHiddenLabel = labelEdge === 'none';
  const { containerAria, hiddenLabelAria, linkAria } = useLabelledLinkAria({
    'aria-describedby': inputProps['aria-describedby'],
    baseId,
    hasEmptyLabel,
    hasHiddenLabel,
    labelId: labelProps.id
  });

  return (
    <FormFieldContext.Provider value={formFieldContext}>
      <ReadonlyTextField
        columnSpan={columnSpan}
        role="presentation"
        inlineUserAssistance={inlineUserAssistance}
        {...fieldLabelProps}>
        <div class={classes} {...containerAria}>
          {hasHiddenLabel && <HiddenLabel {...hiddenLabelAria}>{label}</HiddenLabel>}
          {/**
           * The containing div is a flexbox. So, the text-align will not work for the link
           * content. So, we wrap the link in a 100% block. This way the text align will
           * work as the link is now inside a block and not flexbox.
           */}
          <View width="100%">
            <Link href={href} target={target} onClick={onClick} {...linkAria}>
              <Text size="md" variant="inherit">
                {linkText}
              </Text>
            </Link>
          </View>
        </div>
      </ReadonlyTextField>
    </FormFieldContext.Provider>
  );
};
